<template>
  <div class="editor-wrapper">
    <div class="editor">
      <div class="top-spacing">
        <editor-menu-bar :editor="editor" v-slot="{ commands, isActive }">
          <div class="menubar">
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.bold() }"
              @click="commands.bold">
              <icon name="bold" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.italic() }"
              @click="commands.italic">
              <icon name="italic" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.strike() }"
              @click="commands.strike">
              <icon name="strike" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.underline() }"
              @click="commands.underline">
              <icon name="underline" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.code() }"
              @click="commands.code">
              <icon name="code" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.paragraph() }"
              @click="commands.paragraph">
              <icon name="paragraph" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.heading({ level: 1 }) }"
              @click="commands.heading({ level: 1 })">
              H1
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.heading({ level: 2 }) }"
              @click="commands.heading({ level: 2 })">
              H2
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.heading({ level: 3 }) }"
              @click="commands.heading({ level: 3 })">
              H3
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.bullet_list() }"
              @click="commands.bullet_list">
              <icon name="ul" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.ordered_list() }"
              @click="commands.ordered_list">
              <icon name="ol" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.blockquote() }"
              @click="commands.blockquote">
              <icon name="quote" />
            </button>
            <button
              class="menubar__button"
              :class="{ 'is-active': isActive.code_block() }"
              @click="commands.code_block">
              <icon name="code" />
            </button>
            <button
              class="menubar__button"
              @click="commands.horizontal_rule">
              <icon name="hr" />
            </button>
            <button
              class="menubar__button"
              @click="commands.undo">
              <icon name="undo" />
            </button>
            <button
              class="menubar__button"
              @click="commands.redo">
              <icon name="redo" />
            </button>
            <label
            class="menubar__button">
              <input type="file" @change="uploadImage($event, commands.image)">
              <icon name="image" />
            </label>
            <button
              class="menubar__button"
              @click="commands.createTable({rowsCount: 3, colsCount: 3, withHeaderRow: false })">
              <icon name="table" />
            </button>
            <span v-if="isActive.table()">
              <button
                class="menubar__button"
                @click="commands.deleteTable">
                <icon name="delete_table" />
              </button>
              <button
                class="menubar__button"
                @click="commands.addColumnBefore">
                <icon name="add_col_before" />
              </button>
              <button
                class="menubar__button"
                @click="commands.addColumnAfter">
                <icon name="add_col_after" />
              </button>
              <button
                class="menubar__button"
                @click="commands.deleteColumn">
                <icon name="delete_col" />
              </button>
              <button
                class="menubar__button"
                @click="commands.addRowBefore">
                <icon name="add_row_before" />
              </button>
              <button
                class="menubar__button"
                @click="commands.addRowAfter">
                <icon name="add_row_after" />
              </button>
              <button
                class="menubar__button"
                @click="commands.deleteRow">
                <icon name="delete_row" />
              </button>
              <button
                class="menubar__button"
                @click="commands.toggleCellMerge">
                <icon name="combine_cells" />
              </button>
            </span>
            <button
              class="menubar__button"
              style="margin-left: 60px;"
              @click="updateProject">
              Guardar
            </button>
          </div>
        </editor-menu-bar>
      </div>
      <editor-content style="margin-top: 50px" :editor="editor" />
    </div>
  </div>
</template>

<script>
import axios from 'axios';
import { createNamespacedHelpers } from 'vuex';
import { Editor, EditorContent, EditorMenuBar } from 'tiptap';
import {
  Blockquote,
  CodeBlock,
  HardBreak,
  Heading,
  HorizontalRule,
  OrderedList,
  BulletList,
  ListItem,
  Bold,
  Code,
  Italic,
  Link,
  Strike,
  Underline,
  History,
  Image,
  Table,
  TableHeader,
  TableCell,
  TableRow,
} from 'tiptap-extensions';
import Icon from '@/components/EditorIcon.vue';

const { mapState, mapActions } = createNamespacedHelpers('project');

export default {
  components: {
    EditorContent,
    EditorMenuBar,
    Icon,
  },
  data() {
    return {
      editor: null,
      html: '',
    };
  },
  computed: {
    ...mapState([
      'project',
    ]),
  },
  methods: {
    async updateProject() {
      const { projectId } = this.$route.params;
      const editedData = {};
      editedData.content = this.html;
      await this.editProject({
        id: projectId,
        data: editedData,
      });
      this.$router.push(`/admin/projects/${projectId}`);
    },
    uploadImage(event, command) {
      let src = '';
      const fd = new FormData();
      const file = event.target.files[0];
      const url = `${process.env.VUE_APP_SERVER_URL}/public-upload`;
      fd.append('image', file);
      axios.post(url, fd)
        .then(({ data }) => {
          ({ src } = data);
          if (src !== null) {
            command({ src });
          }
        });
    },
    ...mapActions([
      'getProject',
      'editProject',
    ]),
  },
  beforeDestroy() {
    // Always destroy your editor instance when it's no longer needed
    this.editor.destroy();
  },
  mounted() {
    const { projectId } = this.$route.params;
    this.getProject(projectId).then(() => {
      this.editor = new Editor({
        content: this.project.content,
        onUpdate: ({ getHTML }) => {
          this.html = getHTML();
        },
        extensions: [
          new Blockquote(),
          new BulletList(),
          new CodeBlock(),
          new HardBreak(),
          new Heading({ levels: [1, 2, 3] }),
          new HorizontalRule(),
          new ListItem(),
          new OrderedList(),
          new Link(),
          new Bold(),
          new Code(),
          new Italic(),
          new Strike(),
          new Underline(),
          new History(),
          new Image(),
          new Table({
            resizable: true,
          }),
          new TableHeader(),
          new TableCell(),
          new TableRow(),
        ],
      });
      this.html = this.project.content;
    });
  },
};
</script>

<style lang="scss" scoped>
.editor-wrapper {
  width: 100%;
  display: flex;
  justify-content: center;
}
.editor {
  width: 800px;
  padding-top: 100px;
}
.top-spacing {
  background-color: white;
  position: fixed;
  z-index: 1;
  width: 800px;
  top: 0;
}
.menubar {
  margin-bottom: .5rem;
  transition: visibility 0.2s 0.4s, opacity 0.2s 0.4s;
  background-color: white;
  z-index: 1;
  padding: 8px 0;
  width: 100%;
  margin-top: 90px;

  &.is-hidden {
    visibility: hidden;
    opacity: 0;
  }

  &.is-focused {
    visibility: visible;
    opacity: 1;
    transition: visibility 0.2s, opacity 0.2s;
  }

  &__button {
    font-weight: bold;
    display: inline-flex;
    background: transparent;
    border: 0;
    color: black;
    padding: 0.2rem 0.5rem;
    margin-right: 0.2rem;
    border-radius: 3px;
    cursor: pointer;

    &:hover {
      background-color: rgba(black, 0.05);
    }

    &.is-active {
      background-color: rgba(black, 0.1);
    }
  }

  span#{&}__button {
    font-size: 13.3333px;
  }
}
input[type=file] {
  width: 0.1px;
  height: 0.1px;
  opacity: 0;
  overflow: hidden;
  position: absolute;
  z-index: -1;
}
/deep/ .ProseMirror {
   outline: 0 !important;
}
/deep/ strong {
  font-weight: 500;
}
/deep/ h1 {
  font-size: 2rem;
  margin: 2rem 0 1rem 0;
}
/deep/ h2 {
  font-size: 1.6rem;
  margin: 1rem 0;
}
/deep/ h3 {
  font-size: 1.4rem;
  margin: 1rem 0;
}
/deep/ p {
  margin: 1rem 0;
  text-align: justify !important;
  white-space: normal;
}
/deep/ pre {
  padding: 0.7rem 1rem;
  border-radius: 5px;
  background: black;
  color: white;
  font-size: 0.8rem;
  overflow-x: auto;

  /deep/ code {
    display: block;
  }
}
/deep/ * {
  caret-color: currentColor;
  line-height: 1.5;
  font-family: 'Inter';
}
/deep/ p code {
  display: inline-block;
  padding: 0 0.4rem;
  border-radius: 5px;
  font-size: 0.8rem;
  font-weight: bold;
  background: rgba(black, 0.1);
  color: rgba(black, 0.8);
}
/deep/ ul,
/deep/ ol {
  padding-left: 1rem;
}
/deep/ li > p,
/deep/ li > ol,
/deep/ li > ul {
  margin: 0;
}
/deep/ a {
  color: inherit;
}
/deep/ blockquote {
  border-left: 3px solid rgba(black, 0.1);
  color: rgba(black, 0.8);
  padding-left: 0.8rem;
  font-style: italic;

  p {
    margin: 0;
  }
}
/deep/ img {
  max-width: 100%;
  border-radius: 3px;
}
/deep/ table {
  border-collapse: collapse;
  table-layout: fixed;
  width: 100%;
  margin: 0;
  overflow: hidden;
}
/deep/ td, /deep/ th {
  min-width: 1em;
  border: 1px solid rgb(206, 205, 205);
  padding: 3px 5px;
  vertical-align: top;
  box-sizing: border-box;
  position: relative;
}
/deep/ td > * {
  margin-top: 0;
  margin-bottom: 0;
}
/deep/ th > * {
  margin-top: 0;
  margin-bottom: 0;
}
/deep/ th {
  font-weight: bold;
  text-align: left;
}
/deep/ .selectedCell:after {
  z-index: 2;
  position: absolute;
  content: "";
  left: 0; right: 0; top: 0; bottom: 0;
  background: rgba(200, 200, 255, 0.4);
  pointer-events: none;
}

/deep/ .column-resize-handle {
  position: absolute;
  right: -2px; top: 0; bottom: 0;
  width: 4px;
  z-index: 20;
  background-color: #adf;
  pointer-events: none;
}
/deep/ .tableWrapper {
  margin: 1em 0;
  overflow-x: auto;
}
/deep/ .resize-cursor {
  cursor: ew-resize;
  cursor: col-resize;
}
@media only screen and (max-width: 820px) {
  .editor {
    width: 96%;
    padding-top: 120px;
  }
  .top-spacing {
    width: 96%;
  }
  .menubar {
    display: flex;
    width: 100%;
    flex-wrap: wrap;

    &__button {
      margin-top: 5px;
    }
  }
}
</style>
